import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBackendUrl } from '../../utils/api';

// Ant Design imports
import {
  Typography,
  Input,
  Button,
  Alert,
  Tooltip,
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  ArrowRightOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
  ApiOutlined,
} from '@ant-design/icons';

const { Title, Text, Link } = Typography;

interface LoginResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  admin: {
    id: number;
    username: string;
    email: string;
    full_name?: string;
    role: string;
  };
  session_id?: string;
}

// Global styles for animations
const globalStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0) scale(1); }
    50% { transform: translateY(-20px) scale(1.05); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1); }
    50% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
  }
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes glow {
    0%, 100% { box-shadow: 0 0 20px rgba(22, 119, 255, 0.4), 0 4px 16px rgba(22, 119, 255, 0.3); }
    50% { box-shadow: 0 0 30px rgba(22, 119, 255, 0.6), 0 4px 20px rgba(22, 119, 255, 0.4); }
  }
  @keyframes borderGlow {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }
  .login-card-enter {
    animation: fadeInUp 0.6s ease-out forwards;
  }
  .login-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(22, 119, 255, 0.35), 0 4px 12px rgba(22, 119, 255, 0.2) !important;
  }
  .login-btn:active:not(:disabled) {
    transform: translateY(0);
  }
  .feature-pill:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(22, 119, 255, 0.15);
    border-color: rgba(22, 119, 255, 0.2) !important;
    background: rgba(255, 255, 255, 0.95) !important;
  }
  .back-link:hover {
    color: #1677ff !important;
  }
  @media (max-width: 992px) {
    .login-branding-panel {
      display: none !important;
    }
  }
`;

function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ username?: string; password?: string }>({});
  const [isFocused, setIsFocused] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  // Entrance animation on mount
  useEffect(() => {
    setMounted(true);
    const usernameInput = document.getElementById('username-input');
    if (usernameInput) {
      setTimeout(() => usernameInput.focus(), 600);
    }
  }, []);

  const validateForm = (): boolean => {
    const errors: { username?: string; password?: string } = {};

    if (!username.trim()) {
      errors.username = 'Username or email is required';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 4) {
      errors.password = 'Password must be at least 4 characters';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setFieldErrors({});

    try {
      const response = await fetch(`${getBackendUrl()}/api/auth/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          username: username.trim(),
          password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Login failed' }));
        throw new Error(errorData.detail || errorData.message || 'Login failed');
      }

      const data: LoginResponse = await response.json();

      if (data.admin) {
        // Store admin display info only (tokens are in HttpOnly cookies)
        sessionStorage.setItem('admin_user', JSON.stringify(data.admin));

        // Redirect to admin dashboard
        navigate('/console');
      } else {
        throw new Error('Login failed - no admin data received');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
      setLoading(false);
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    if (fieldErrors.username) {
      setFieldErrors(prev => ({ ...prev, username: undefined }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (fieldErrors.password) {
      setFieldErrors(prev => ({ ...prev, password: undefined }));
    }
  };

  const features = [
    { icon: <SafetyCertificateOutlined />, text: 'Enterprise Security' },
    { icon: <ThunderboltOutlined />, text: 'AI-Powered Search' },
    { icon: <GlobalOutlined />, text: 'Global Catalog' },
    { icon: <ApiOutlined />, text: 'Multi-Source APIs' },
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>{globalStyles}</style>

      {/* Animated background elements */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {/* Gradient orbs */}
        <div
          style={{
            position: 'absolute',
            top: '5%',
            left: '10%',
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(22, 119, 255, 0.12) 0%, transparent 70%)',
            filter: 'blur(80px)',
            animation: 'float 8s ease-in-out infinite',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '10%',
            right: '5%',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(114, 46, 209, 0.08) 0%, transparent 70%)',
            filter: 'blur(100px)',
            animation: 'float 10s ease-in-out infinite reverse',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '30%',
            width: 700,
            height: 700,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(82, 196, 26, 0.06) 0%, transparent 70%)',
            filter: 'blur(120px)',
            transform: 'translate(-50%, -50%)',
            animation: 'pulse 6s ease-in-out infinite',
          }}
        />
        {/* Grid pattern */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
        {/* Subtle radial gradient overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(ellipse at 70% 50%, rgba(255,255,255,0.8) 0%, transparent 70%)',
          }}
        />
      </div>

      {/* Left Panel - Branding */}
      <div
        style={{
          flex: '1 1 55%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '60px 80px',
          position: 'relative',
          zIndex: 1,
        }}
        className="login-branding-panel"
      >
        {/* Logo & Brand */}
        <div style={{ marginBottom: 56 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 14,
              padding: '14px 24px',
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              borderRadius: 20,
              border: '1px solid rgba(0, 0, 0, 0.06)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              marginBottom: 40,
            }}
          >
            <img
              src="/images/partsgpt.png"
              alt="PartsGPT Logo"
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                objectFit: 'contain',
              }}
            />
            <span
              style={{
                fontSize: 26,
                fontWeight: 700,
                color: '#1a1a2e',
                letterSpacing: '-0.01em',
              }}
            >
              PartsGPT
            </span>
          </div>

          <Title
            level={1}
            style={{
              margin: 0,
              fontSize: 48,
              fontWeight: 700,
              lineHeight: 1.2,
              color: '#1a1a2e',
              letterSpacing: '-0.02em',
            }}
          >
            Intelligent Parts
            <br />
            <span
              style={{
                background: 'linear-gradient(135deg, #1677ff, #722ed1)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Discovery Platform
            </span>
          </Title>

          <Text
            style={{
              display: 'block',
              marginTop: 24,
              fontSize: 18,
              color: '#64748b',
              lineHeight: 1.7,
              maxWidth: 520,
              fontWeight: 400,
            }}
          >
            Transform your automotive parts search with AI-powered intelligence,
            multi-source catalog integration, and real-time analytics.
          </Text>
        </div>

        {/* Feature Pills */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {features.map((feature, index) => (
            <div
              key={index}
              className="feature-pill"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 20px',
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: 100,
                border: '1px solid rgba(0, 0, 0, 0.06)',
                fontSize: 14,
                color: '#475569',
                fontWeight: 500,
                cursor: 'default',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              }}
            >
              <span style={{ color: '#1677ff', fontSize: 16 }}>{feature.icon}</span>
              {feature.text}
            </div>
          ))}
        </div>

        {/* Stats row */}
        <div
          style={{
            display: 'flex',
            gap: 48,
            marginTop: 64,
            paddingTop: 32,
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          }}
        >
          {[
            { value: '10M+', label: 'Parts Indexed' },
            { value: '99.9%', label: 'Uptime SLA' },
            { value: '<100ms', label: 'Avg Response' },
          ].map((stat, index) => (
            <div key={index}>
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: 4,
                }}
              >
                {stat.value}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div
        style={{
          flex: '1 1 45%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div
          className={mounted ? 'login-card-enter' : ''}
          style={{
            width: '100%',
            maxWidth: 440,
            padding: '52px 44px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(40px)',
            borderRadius: 28,
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.08), 0 4px 24px rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            opacity: mounted ? 1 : 0,
          }}
        >
          {/* Form Header */}
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <img
              src="/images/partsgpt.png"
              alt="PartsGPT Logo"
              style={{
                width: 64,
                height: 64,
                margin: '0 auto 20px',
                borderRadius: 16,
                objectFit: 'contain',
              }}
            />
            <Title
              level={2}
              style={{
                margin: 0,
                marginBottom: 8,
                fontSize: 28,
                fontWeight: 700,
                color: '#1a1a2e',
              }}
            >
              Welcome back
            </Title>
            <Text style={{ color: '#8c8c8c', fontSize: 15 }}>
              Sign in to access your admin dashboard
            </Text>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError('')}
              style={{
                marginBottom: 24,
                borderRadius: 12,
                border: 'none',
                background: 'linear-gradient(135deg, #fff2f0 0%, #ffebe8 100%)',
              }}
            />
          )}

          {/* Login Form */}
          <form onSubmit={handleLogin}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {/* Username Field */}
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: 10,
                    fontSize: 13,
                    fontWeight: 600,
                    color: isFocused === 'username' ? '#1677ff' : '#434343',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    transition: 'color 0.3s ease',
                  }}
                >
                  Username or Email
                </label>
                <Input
                  id="username-input"
                  placeholder="Enter your username"
                  size="large"
                  value={username}
                  onChange={handleUsernameChange}
                  onFocus={() => setIsFocused('username')}
                  onBlur={() => setIsFocused(null)}
                  disabled={loading}
                  status={fieldErrors.username ? 'error' : undefined}
                  prefix={
                    <UserOutlined
                      style={{
                        color: fieldErrors.username ? '#ff4d4f' : isFocused === 'username' ? '#1677ff' : '#bfbfbf',
                        transition: 'color 0.3s ease',
                        fontSize: 16,
                      }}
                    />
                  }
                  autoComplete="username"
                  style={{
                    height: 56,
                    borderRadius: 14,
                    fontSize: 15,
                    background: isFocused === 'username' ? '#fff' : '#f8fafc',
                    border: isFocused === 'username' ? '2px solid #1677ff' : '2px solid transparent',
                    boxShadow: isFocused === 'username'
                      ? '0 0 0 4px rgba(22, 119, 255, 0.12), 0 2px 8px rgba(0, 0, 0, 0.04)'
                      : '0 2px 8px rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.3s ease',
                  }}
                />
                {fieldErrors.username && (
                  <Text type="danger" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                    {fieldErrors.username}
                  </Text>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: 10,
                    fontSize: 13,
                    fontWeight: 600,
                    color: isFocused === 'password' ? '#1677ff' : '#434343',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    transition: 'color 0.3s ease',
                  }}
                >
                  Password
                </label>
                <Input
                  id="password-input"
                  placeholder="Enter your password"
                  size="large"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  onFocus={() => setIsFocused('password')}
                  onBlur={() => setIsFocused(null)}
                  disabled={loading}
                  status={fieldErrors.password ? 'error' : undefined}
                  prefix={
                    <LockOutlined
                      style={{
                        color: fieldErrors.password ? '#ff4d4f' : isFocused === 'password' ? '#1677ff' : '#bfbfbf',
                        transition: 'color 0.3s ease',
                        fontSize: 16,
                      }}
                    />
                  }
                  suffix={
                    <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                      <Button
                        type="text"
                        size="small"
                        icon={showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                        style={{
                          border: 'none',
                          padding: 0,
                          height: 'auto',
                          color: '#8c8c8c',
                        }}
                      />
                    </Tooltip>
                  }
                  autoComplete="current-password"
                  style={{
                    height: 56,
                    borderRadius: 14,
                    fontSize: 15,
                    background: isFocused === 'password' ? '#fff' : '#f8fafc',
                    border: isFocused === 'password' ? '2px solid #1677ff' : '2px solid transparent',
                    boxShadow: isFocused === 'password'
                      ? '0 0 0 4px rgba(22, 119, 255, 0.12), 0 2px 8px rgba(0, 0, 0, 0.04)'
                      : '0 2px 8px rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.3s ease',
                  }}
                />
                {fieldErrors.password && (
                  <Text type="danger" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                    {fieldErrors.password}
                  </Text>
                )}
              </div>

              {/* Sign In Button */}
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={loading}
                className="login-btn"
                style={{
                  height: 56,
                  borderRadius: 14,
                  fontSize: 16,
                  fontWeight: 600,
                  marginTop: 8,
                  background: 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)',
                  backgroundSize: '200% auto',
                  border: 'none',
                  boxShadow: '0 4px 20px rgba(22, 119, 255, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  transition: 'all 0.3s ease',
                }}
              >
                {loading ? 'Signing in...' : (
                  <>
                    Sign In
                    <ArrowRightOutlined style={{ fontSize: 14 }} />
                  </>
                )}
              </Button>
            </div>
          </form>

          {/* Footer */}
          <div style={{ marginTop: 32, textAlign: 'center' }}>
            <Link
              onClick={() => navigate('/')}
              className="back-link"
              style={{
                color: '#8c8c8c',
                fontSize: 14,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                transition: 'color 0.3s ease',
                fontWeight: 500,
              }}
            >
              <span style={{ transform: 'rotate(180deg)', display: 'inline-block' }}>
                <ArrowRightOutlined />
              </span>
              Back to Home
            </Link>
          </div>

          {/* Security Badge */}
          <div
            style={{
              marginTop: 28,
              padding: '14px 20px',
              background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              border: '1px solid rgba(82, 196, 26, 0.2)',
            }}
          >
            <SafetyCertificateOutlined style={{ color: '#52c41a', fontSize: 18 }} />
            <Text style={{ color: '#389e0d', fontSize: 13, fontWeight: 500 }}>
              Secured with enterprise-grade encryption
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;
